import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// Layout colonne per-utente (ordine + larghezza) salvato nel DB via /me/preferences/table/{key}.
export function useColumnLayout(tableKey, columns) {
  const ids = columns.map((c) => c.id);
  const idsKey = ids.join("|");
  const defaultWidths = {};
  columns.forEach((c) => { defaultWidths[c.id] = c.width || 160; });

  const [order, setOrder] = useState(ids);
  const [widths, setWidths] = useState(defaultWidths);
  const saveTimer = useRef(null);
  const widthsRef = useRef(widths);
  const orderRef = useRef(order);
  widthsRef.current = widths;
  orderRef.current = order;

  useEffect(() => {
    let active = true;
    api.get(`/me/preferences/table/${tableKey}`).then(({ data }) => {
      if (!active) return;
      const savedOrder = Array.isArray(data.order) ? data.order.filter((id) => ids.includes(id)) : [];
      // Inserisce le colonne nuove nella loro posizione dichiarata (dopo il predecessore già presente)
      const merged = savedOrder.slice();
      ids.forEach((id, idx) => {
        if (merged.includes(id)) return;
        let insertAt = merged.length;
        for (let j = idx - 1; j >= 0; j--) {
          const pos = merged.indexOf(ids[j]);
          if (pos !== -1) { insertAt = pos + 1; break; }
        }
        if (idx === 0) insertAt = 0;
        merged.splice(insertAt, 0, id);
      });
      setOrder(merged);
      setWidths({ ...defaultWidths, ...(data.widths || {}) });
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey, idsKey]);

  const persist = useCallback((nextOrder, nextWidths) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put(`/me/preferences/table/${tableKey}`, { order: nextOrder, widths: nextWidths, hidden: [] }).catch(() => {});
    }, 500);
  }, [tableKey]);

  const setWidth = useCallback((id, px) => {
    const next = { ...widthsRef.current, [id]: Math.max(60, Math.round(px)) };
    setWidths(next);
    persist(orderRef.current, next);
  }, [persist]);

  const moveColumn = useCallback((dragId, overId) => {
    if (dragId === overId) return;
    const prev = orderRef.current;
    const from = prev.indexOf(dragId), to = prev.indexOf(overId);
    if (from === -1 || to === -1) return;
    const next = [...prev];
    next.splice(from, 1);
    next.splice(to, 0, dragId);
    setOrder(next);
    persist(next, widthsRef.current);
  }, [persist]);

  const orderedColumns = order.map((id) => columns.find((c) => c.id === id)).filter(Boolean);
  return { orderedColumns, widths, setWidth, moveColumn };
}
